import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import "react-phone-input-2/lib/style.css";
import ControlledInput, {
  FieldErrorText,
} from "../../../components/inputs/ControlledInput";
import ControlledEmailInput from "../../../components/inputs/ControlledEmailInput";
import ControlledPhoneInput from "../../../components/inputs/ControlledPhoneInput";

import { ValidationConstants } from "../../../app/constants/ValidationConstants";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { validatePhoneFormat } from "../../../constants/constants";
import { UpdateOrgDto } from "../../../factories/org/factory-dtos";
import { orgUpdateFactory } from "../../../factories/org/update-org.factory";
import { SettingsInput } from "../../../components/inputs/SettingsInput";
import {
  useGetOrgQuery,
  useUpdateOrgMutation,
} from "../../../services/OrgService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { GetPageTitle } from "../../../support/ScrollToTop";
import { Typography } from "../../../components/typography/TypographyComponents";
import { buttonTokens } from "../../../theme/designTokens";
import { validateEmail } from "../../../utils/email";
import { notBlank, trimmed } from "../../../utils/notBlank";
import { FieldLabel } from "../../../components/newUI/text";
import { CountryCode, REGIONS, countryCodeOf } from "../../region/region";
import { useIsFirmAdmin } from "../../region/useFirmRegion";
import { CountryCard } from "./CountryCard";

export const EditCompany: FC = () => {
  const navigate = useNavigate();

  const { data: org, isLoading: isLoadingOrg } = useGetOrgQuery(
    getLinkedFirm()?.orgId || "",
  );

  const [
    updateOrd,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdated },
  ] = useUpdateOrgMutation();

  const isAdmin = useIsFirmAdmin();
  // Picked country (Admins only); null = keep the saved value as it is.
  const [pickedCountry, setPickedCountry] = useState<CountryCode | null>(null);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<UpdateOrgDto>({
    defaultValues: {
      id: getLinkedFirm()?.orgId,
      email: org?.email,
      locationId: org?.locations?.[0]?.locationAddress?.id,
      locationsId: org?.locations?.[0]?.id,
      locationAddressId: org?.locations?.[0]?.locationAddress?.id,
    },
  });

  const onSubmit = (data: UpdateOrgDto) => {
    if (org) {
      // The ids are read from the loaded firm at save time: useForm's
      // defaultValues are only read on the first render, which (when this page
      // is opened directly or refreshed) happens before the firm has loaded,
      // so the address was silently not saved (QA 2026-10-21).
      const location = org.locations?.[0];
      const savedCountry = location?.locationAddress?.country || "";
      const submitData = orgUpdateFactory.createModelFromDto({
        ...data,
        // Only Admins change the country (the server checks too); anyone else
        // sends the saved value back untouched.
        country:
          isAdmin && pickedCountry
            ? REGIONS[pickedCountry].name
            : savedCountry,
        legalName: trimmed(data.legalName),
        displayName: trimmed(data.displayName),
        id: data.id || getLinkedFirm()?.orgId || "",
        locationId: location?.locationAddress?.id ?? data.locationId,
        locationsId: location?.id ?? data.locationsId,
        locationAddressId:
          location?.locationAddress?.id ?? data.locationAddressId,
      });
      updateOrd(submitData);
    }
  };

  useEffect(() => {
    document.title = GetPageTitle("Edit Company Details");
  }, []);

  useEffect(() => {
    if (isSuccessUpdated) {
      navigate("../");
    }
  }, [isSuccessUpdated]);

  if (isLoadingOrg) {
    return <LottieLoading />;
  }

  const savedAddress = org?.locations?.[0]?.locationAddress;
  const countryCode = pickedCountry ?? countryCodeOf(savedAddress?.country);
  const region = REGIONS[countryCode];

  return (
    <>
      <div className="mb-[120px] py-6">
        <div className="mb-6 flex items-center justify-between gap-4 font-Manrope">
          <Typography fontcss="subtitle2" colorcss="bodyprimary">
            Firm Details
          </Typography>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className={buttonTokens.whiteButton}
            >
              Cancel
            </button>
            <button
              disabled={isLoadingUpdate}
              onClick={() => handleSubmit(onSubmit)()}
              className={buttonTokens.purpleButton}
            >
              Save Changes
            </button>
          </div>
        </div>

        <CountryCard
          country={countryCode}
          isAdmin={isAdmin}
          onChange={(code) => {
            if (code === countryCode) return;
            setPickedCountry(code);
            // A province of one country isn't a state of another.
            const list = REGIONS[code].regions;
            const current = String(getValues("region") || "");
            if (list && !list.some((r) => r.value === current))
              setValue("region", "", { shouldDirty: true });
          }}
        />

        <div className="mt-6 flex items-start justify-between rounded-xl border border-[#E4E9F2] bg-white p-6 max-md:flex-col max-md:gap-2 max-md:p-4">
          <div className="w-[42%] max-md:w-full">
            <Typography fontcss="subtitle3" colorcss="bodyprimary">
              General Details
            </Typography>
            <Typography fontcss="cardSub" colorcss="bodytertiary">
              Basic information about this company
            </Typography>
          </div>
          <div className="grid flex-1 grid-cols-2 gap-x-8 max-md:w-full max-md:grid-cols-1 max-md:gap-x-0 max-md:[overflow-wrap:anywhere]">
            <div className="py-3">
              <ControlledInput
                control={control}
                name={"legalName"}
                label={"Company Name"}
                placeholder={"Company Name..."}
                defaultValue={org?.legalName || ""}
                rules={{
                  required: "This field is required",
                  validate: notBlank(),
                  ...ValidationConstants.orgRules.nameRule.valueLength,
                }}
              />
            </div>
            <div className="py-3">
              <ControlledInput
                control={control}
                name={"displayName"}
                label={"Invoice Display Name"}
                placeholder={"Invoice Display Name..."}
                defaultValue={org?.displayName || ""}
                rules={{
                  required: "This field is required",
                  validate: notBlank(),
                  ...ValidationConstants.orgRules.nameRule.valueLength,
                }}
              />
            </div>
            <div className="py-3">
              <ControlledEmailInput
                control={control}
                name={"email"}
                label={"Email"}
                placeholder={"Email..."}
                defaultValue={org?.email || ""}
                rules={{
                  validate: validateEmail("Invalid email address format"),
                }}
              />
            </div>
            <div className="py-3">
              <ControlledPhoneInput
                control={control}
                name={"phoneNumber"}
                label={"Phone Number"}
                defaultValue={org?.phoneNumbers?.[0]?.number || ""}
                rules={{
                  validate: {
                    phoneFormat: (value: string) =>
                      value
                        ? validatePhoneFormat(value) ||
                          "Invalid phone number format"
                        : true,
                  },
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-start justify-between rounded-xl border border-[#E4E9F2] bg-white p-6 max-md:flex-col max-md:gap-2 max-md:p-4">
          <div className="w-[42%] max-md:w-full">
            <Typography fontcss="subtitle3" colorcss="bodyprimary">
              Address
            </Typography>
            <Typography fontcss="cardSub" colorcss="bodytertiary">
              Company address information
            </Typography>
          </div>
          <div className="grid flex-1 grid-cols-2 gap-x-8 max-md:w-full max-md:grid-cols-1 max-md:gap-x-0 max-md:[overflow-wrap:anywhere]">
            <div className="col-span-2 py-3 max-md:col-span-1">
              <Controller
                control={control}
                defaultValue={org?.locations?.[0]?.locationAddress?.line1 || ""}
                rules={{
                  required: "This field is required",
                  ...ValidationConstants.orgRules.nameRule.valueLength,
                }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <div>
                    <SettingsInput
                      value={value}
                      isError={!!error}
                      onChange={(e) => onChange(e.target.value)}
                      label={"Address"}
                      placeholder={"Address..."}
                      inputClasses={"w-full"}
                    />
                    <FieldErrorText message={error?.message} />
                  </div>
                )}
                name={"line1"}
              />
            </div>
            <div className="py-3">
              <ControlledInput
                control={control}
                name={"city"}
                label={region.cityLabel}
                placeholder={`${region.cityLabel}...`}
                defaultValue={org?.locations?.[0]?.locationAddress?.city || ""}
                rules={{
                  required: "This field is required",
                  ...ValidationConstants.orgRules.nameRule.valueLength,
                }}
              />
            </div>
            <div className="py-3">
              {region.regions ? (
                <Controller
                  control={control}
                  name={"region"}
                  defaultValue={savedAddress?.region || ""}
                  rules={{ required: "This field is required" }}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => {
                    const options = region.regions || [];
                    // An older typed value that isn't in the list stays
                    // pickable so saving doesn't lose it.
                    const unknown =
                      !!value && !options.some((o) => o.value === value);
                    return (
                      <div>
                        <FieldLabel
                          label={region.regionLabel}
                          className={"mb-1 text-[#5F758F]"}
                        />
                        <select
                          aria-label={region.regionLabel}
                          value={value || ""}
                          onChange={(e) => onChange(e.target.value)}
                          className={`h-10 w-full rounded-lg border bg-white px-3 text-sm font-medium text-[#1D2C3B] outline-none transition-all duration-300 focus:border-[#6938EF] focus:shadow-settings-purple ${
                            error ? "border-[#DC2626]" : "border-[#C8D2DE]"
                          }`}>
                          <option value={""} disabled>
                            {`Select ${region.regionLabel.toLowerCase()}...`}
                          </option>
                          {unknown && <option value={value}>{value}</option>}
                          {options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                        <FieldErrorText message={error?.message} />
                      </div>
                    );
                  }}
                />
              ) : (
                <ControlledInput
                  control={control}
                  name={"region"}
                  label={region.regionLabel}
                  placeholder={`${region.regionLabel}...`}
                  defaultValue={savedAddress?.region || ""}
                  rules={{
                    ...ValidationConstants.orgRules.nameRule.valueLength,
                  }}
                />
              )}
            </div>
            <div className="py-3">
              <ControlledInput
                control={control}
                name={"postalCode"}
                label={region.postalLabel}
                placeholder={`${region.postalLabel}...`}
                defaultValue={
                  org?.locations?.[0]?.locationAddress?.postalCode || ""
                }
                rules={{
                  required: "This field is required",
                  ...ValidationConstants.orgRules.nameRule.valueLength,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
